import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import Footer from "@/components/Footer";

export function Section({ title, id, children }) {
  return (
    <section id={id} className="scroll-mt-24" data-testid={id ? `legal-section-${id}` : undefined}>
      {title && (
        <h2 className="font-display text-xl sm:text-2xl font-semibold text-white tracking-tight mt-10 mb-3">
          {title}
        </h2>
      )}
      <div className="space-y-3 text-sm sm:text-[15px] text-zinc-400 leading-relaxed">
        {children}
      </div>
    </section>
  );
}

export function Bullets({ items }) {
  return (
    <ul className="list-disc pl-5 space-y-2 marker:text-lime-400">
      {items.map((t, i) => (
        <li key={i}>{t}</li>
      ))}
    </ul>
  );
}

export default function LegalLayout({ title, subtitle, lastUpdated, children }) {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-[#0A0D0E] grain-overlay flex flex-col" data-testid="legal-page">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-[#0F1315]/90 backdrop-blur-xl border-b border-[#242B30]">
        <div className="mx-auto w-full max-w-4xl px-5 sm:px-8 py-4 flex items-center gap-3">
          <Link to="/" data-testid="legal-home-link" className="flex items-center gap-2.5 min-w-0">
            <img src="/karyow-logo.png" alt="KARYOW" className="h-9 w-9 rounded-xl object-cover shrink-0" />
            <div className="min-w-0">
              <div className="font-display font-bold text-white leading-none truncate">KARYOW</div>
              <div className="font-mono text-[9px] tracking-[0.22em] text-lime-400 uppercase mt-1">AI Ads Manager</div>
            </div>
          </Link>
          <div className="flex-1" />
          <Link
            to="/"
            data-testid="legal-back-link"
            className="inline-flex items-center gap-2 rounded-full border border-[#242B30] bg-[#14181B] hover:border-lime-400/40 px-4 py-2 text-sm text-zinc-300 hover:text-white transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            <span className="hidden sm:inline">Back to app</span>
            <span className="sm:hidden">Back</span>
          </Link>
        </div>
      </header>

      {/* Body */}
      <main className="flex-1 w-full">
        <div className="mx-auto w-full max-w-4xl px-5 sm:px-8 py-10 sm:py-14">
          <div className="font-mono text-[11px] tracking-[0.18em] uppercase text-lime-400 mb-3">Legal</div>
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white leading-[1.1]">
            {title}
          </h1>
          {subtitle && <p className="mt-4 text-zinc-400 leading-relaxed text-sm sm:text-base max-w-2xl">{subtitle}</p>}
          {lastUpdated && (
            <p className="mt-4 text-xs text-zinc-600 font-mono">Last updated: {lastUpdated}</p>
          )}

          <div className="mt-6">{children}</div>

          <Footer />
        </div>
      </main>
    </div>
  );
}
